import { FC, useEffect } from 'react'

import styled from 'styled-components'
import { Controller, useForm } from 'react-hook-form'
import { notifications } from '@mantine/notifications'

import { PageTitle } from 'src/Components/PageTitle/index.tsx'
import {
  useGetAllSettingsQuery,
  useUpdateSettingMutation,
} from 'src/Redux/api/settingsApi.ts'
import { Input } from 'src/Components/Input/index.tsx'
import { Button } from 'src/Components/Button'
import { Textarea } from 'src/Components/Textarea/index.tsx'
import { device } from 'src/Helpers/constants.ts'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 36px;
  position: relative;
  height: 100%;
  overflow: hidden;
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 50px;
  position: static;

  @media ${device.tabletMax} {
    gap: 20px;
  }
`

const InnerContainer = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media ${device.tabletMax} {
    width: 100%;
  }
`

const StyledButton = styled(Button)`
  width: 200px;
`

interface SettingsFormData {
  medicalExaminationCost: number
  privilegesData: string
  introductionText: string
  vatRate: number
}

export const SettingsPage: FC = () => {
  const { data: settingsList } = useGetAllSettingsQuery()
  const [updateSetting] = useUpdateSettingMutation()

  const medicalExaminationCost = settingsList?.find((item) => {
    return item.name === 'medicalExaminationCost'
  })?.value

  const privilegesData = settingsList?.find((item) => {
    return item.name === 'privilegesData'
  })?.value

  const introductionText = settingsList?.find((item) => {
    return item.name === 'introductionText'
  })?.value

  const vatRate = settingsList?.find((item) => {
    return item.name === 'vatRate'
  })?.value

  const { control, handleSubmit, reset } = useForm<SettingsFormData>({
    defaultValues: {
      medicalExaminationCost: Number(medicalExaminationCost || 0),
    },
  })

  useEffect(() => {
    if (medicalExaminationCost || settingsList) {
      const privilegesDataFormatted = Array.isArray(privilegesData)
        ? privilegesData.join('\n')
        : ''

      reset({
        medicalExaminationCost: Number(medicalExaminationCost),
        privilegesData: privilegesDataFormatted,
        introductionText: String(introductionText),
        vatRate: Number(vatRate ?? 22),
      })
    }
  }, [
    medicalExaminationCost,
    settingsList,
    reset,
    privilegesData,
    introductionText,
    vatRate,
  ])

  const onSave = (data: SettingsFormData): void => {
    const privilegesArray = data?.privilegesData
      .split('\n')
      .map((str) => str.trim())
      .filter(Boolean)

    updateSetting({
      name: 'privilegesData',
      value: privilegesArray,
    })
      .unwrap()
      .then(() => {
        notifications.show({
          title: 'Настройки успешно сохранены',
          message: '',
          position: 'top-right',
        })
      })

    updateSetting({
      name: 'medicalExaminationCost',
      value: data?.medicalExaminationCost,
    })

    updateSetting({
      name: 'introductionText',
      value: data?.introductionText,
    })

    updateSetting({
      name: 'vatRate',
      value: Number(data?.vatRate) || 0,
    })
  }

  return (
    <Container>
      <TitleWrapper>
        <PageTitle>Настройки КП</PageTitle>
      </TitleWrapper>

      <InnerContainer>
        <Controller
          name='medicalExaminationCost'
          control={control}
          render={({ field: { onChange, value } }) => (
            <Input
              placeholder={'Стоимость медицинского обследования'}
              label={'Стоимость медицинского обследования'}
              value={value}
              onChange={onChange}
              type='number'
              data-testid='medical-examination-cost'
            />
          )}
        />

        <Controller
          name='vatRate'
          control={control}
          render={({ field: { onChange, value } }) => (
            <Input
              placeholder={'Налоговая ставка НДС, %'}
              label={'Налоговая ставка НДС, %'}
              value={value}
              onChange={onChange}
              type='number'
              data-testid='vat-rate'
            />
          )}
        />

        <Controller
          name='privilegesData'
          control={control}
          render={({ field: { onChange, value } }) => (
            <Textarea
              placeholder={'Персональные привилегии'}
              label={'Персональные привилегии'}
              value={value}
              onChange={onChange}
              resize='vertical'
              data-testid='privileges-data'
            />
          )}
        />

        <Controller
          name='introductionText'
          control={control}
          render={({ field: { onChange, value } }) => (
            <Textarea
              placeholder={'Введение'}
              label={'Введение'}
              value={value}
              onChange={onChange}
              resize='vertical'
              data-testid='introduction-text'
            />
          )}
        />
        <StyledButton
          onClick={handleSubmit(onSave)}
          data-testid='save-settings'
        >
          Сохранить
        </StyledButton>
      </InnerContainer>
    </Container>
  )
}
